import { Box, Typography } from "@mui/material";
import usePurchaseHistory from "./hooks/usePurchaseHistory";
import Loading from "./components/Loading/Loading";
import Status from "./components/Status/Status";
import { formatDate } from "./utils/date";
import DownloadButton from "./components/DownloadButton/DownloadButton";
import { useTranslation } from "react-i18next";
import { semanticColors } from "./utils/colors";
import { typography } from "./utils/typography";

const COL = {
  date: { width: "149px", flexShrink: 0, justifyContent: "flex-start" },
  name: { flex: 1, minWidth: 0, justifyContent: "flex-start" },
  status: { width: "177px", flexShrink: 0, justifyContent: "center" },
  value: { width: "118px", flexShrink: 0, justifyContent: "flex-end" },
  receipt: { width: "142px", flexShrink: 0, justifyContent: "center" }
};

function HeaderCell({
  col,
  children
}: {
  col: any;
  children: React.ReactNode;
}) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", px: "8px", ...col }}>
      <Typography
        sx={{ ...typography.body.lBold, color: semanticColors.text.inverse }}
      >
        {children}
      </Typography>
    </Box>
  );
}

function DataCell({ col, children }: { col: any; children: React.ReactNode }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", px: "8px", ...col }}>
      {children}
    </Box>
  );
}

export default function PaymentsHistory() {
  const { data, loading } = usePurchaseHistory();
  const { t } = useTranslation();

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        px: "24px",
        py: "48px",
        gap: "48px",
        width: "100%"
      }}
    >
      <Typography
        sx={{
          ...typography.heading.xl,
          color: semanticColors.text.primary,
          textAlign: "center",
          alignSelf: "stretch"
        }}
      >
        {t("paymentsHistory.title")}
      </Typography>

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" mt={5}>
          <Loading />
        </Box>
      ) : !!(data as any)?.length ? (
        <Box
          sx={{
            width: "100%",
            background: "#FFFFFF",
            boxShadow: "0px 0px 4px rgba(30, 39, 79, 0.12)",
            borderRadius: "24px",
            overflow: "hidden"
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              height: "48px",
              gap: "8px",
              background: semanticColors.gradient.surface
            }}
          >
            <Box sx={{ width: "45px", flexShrink: 0 }} />
            <HeaderCell col={COL.date}>{t("paymentsHistory.date")}</HeaderCell>
            <HeaderCell col={COL.name}>
              {t("paymentsHistory.planName")}
            </HeaderCell>
            <HeaderCell col={COL.status}>
              {t("paymentsHistory.status")}
            </HeaderCell>
            <HeaderCell col={COL.value}>
              {t("paymentsHistory.value")}
            </HeaderCell>
            <HeaderCell col={COL.receipt}>
              {t("paymentsHistory.receipt")}
            </HeaderCell>
          </Box>

          {/* Rows */}
          {(data as any).map((row: any) => (
            <Box
              key={row.id}
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                height: "54px",
                gap: "8px",
                background: semanticColors.surface.canvas,
                borderTop: `0.5px solid ${semanticColors.border.light}`,
                borderBottom: `0.5px solid ${semanticColors.border.light}`
              }}
            >
              <Box sx={{ width: "45px", flexShrink: 0 }} />
              <DataCell col={COL.date}>
                <Typography
                  sx={{
                    ...typography.body.l,
                    color: semanticColors.text.primary,
                    whiteSpace: "nowrap"
                  }}
                >
                  {formatDate(row.createdAt)}
                </Typography>
              </DataCell>
              <DataCell col={COL.name}>
                <Typography
                  sx={{
                    ...typography.body.l,
                    color: semanticColors.text.primary,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap"
                  }}
                >
                  {row.planName}
                </Typography>
              </DataCell>
              <DataCell col={COL.status}>
                <Status
                  urlPending={row.transaction.urlPending}
                  status={row.transaction.status}
                />
              </DataCell>
              <DataCell col={COL.value}>
                <Typography
                  sx={{
                    ...typography.body.xlBold,
                    color: semanticColors.text.primary,
                    whiteSpace: "nowrap"
                  }}
                >
                  {new Intl.NumberFormat("pt-PT", {
                    style: "currency",
                    currency: "EUR"
                  }).format(row.transaction.value)}
                </Typography>
              </DataCell>
              <DataCell col={COL.receipt}>
                {row.transaction.status === "paid" && (
                  <DownloadButton url={row.transaction.urlDocument} />
                )}
              </DataCell>
            </Box>
          ))}
        </Box>
      ) : (
        <Typography
          sx={{ ...typography.body.l, color: semanticColors.text.primary }}
        >
          {t("common.noResults")}
        </Typography>
      )}
    </Box>
  );
}
